<template>
  <div class="team-compact">
    <button
      v-for="m in members"
      :key="m.key"
      class="team-chip"
      type="button"
      @mouseenter="onChipEnter(m, item, $event)"
      @mouseleave="onChipLeave"
      @click.stop="toggleTeamLock(m, item, $event)"
    >
      <div class="chip-avatar">
        <img
          v-if="photoUrl(m.azid)"
          :src="photoUrl(m.azid)"
          @error="$emit('img-error', m.azid)"
        />
        <div v-else class="chip-fallback">{{ initials(m.name) }}</div>
      </div>

      <div class="chip-role">{{ m.key.toUpperCase() }}</div>
    </button>

    <div
      v-if="popover.open"
      class="team-popover-portal"
      :style="{ top: popover.y + 'px', left: popover.x + 'px' }"
      @mouseenter="onPopoverEnter"
      @mouseleave="onPopoverLeave"
    >
      <div class="popover-head">
        <div class="popover-avatar">
          <img
            v-if="photoUrl(popover.member.azid)"
            :src="photoUrl(popover.member.azid)"
            @error="$emit('img-error', popover.member.azid)"
          />
          <div v-else class="chip-fallback">{{ initials(popover.member.name) }}</div>
        </div>

        <div class="popover-meta">
          <div class="popover-role">{{ popover.member.role }}</div>
          <div class="popover-name">{{ popover.member.name }}</div>
        </div>
      </div>

      <div class="popover-actions">
        <button class="popover-btn" @click="$emit('open-teams', popover.member)">
          <i class="fa-brands fa-microsoft"></i>
          Teams
        </button>

        <button class="popover-btn" @click="$emit('open-email', popover.member, popover.item)">
          <i class="fa-duotone fa-envelope"></i>
          Email
        </button>
      </div>

      <div class="popover-foot" v-if="popover.locked">
        <i class="fa-duotone fa-lock"></i>
        Locked (click outside to close)
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TeamCompactCell",
  props: {
    item: { type: Object, required: true },
    photoUrl: { type: Function, required: true }
  },
  data() {
    return {
      popover: {
        open: false,
        locked: false,
        x: 0,
        y: 0,
        member: null,
        item: null
      },
      popoverHover: false,
      hoveringChip: false,
      hoverCloseTimer: null
    }
  },
  computed: {
    members() {
      const item = this.item || {}
      return [
        { key: "lo", role: "Loan Officer", azid: item.loazid, name: item.loname, teamsUpn: item.loemail },
        { key: "pr", role: "Processor", azid: item.procazid, name: item.procName, teamsUpn: item.procemail },
        { key: "uw", role: "Underwriter", azid: item.uwid, name: item.uwname, teamsUpn: item.uwemail }
      ].filter(x => x.name && x.teamsUpn)
    }
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick)
  },
  methods: {
    initials(name) {
      const s = String(name || "").trim()
      if (!s) return "?"
      const parts = s.split(/\s+/).filter(Boolean)
      const a = parts[0]?.[0] || ""
      const b = parts[1]?.[0] || ""
      return (a + b).toUpperCase() || "?"
    },

    positionPopover(event) {
      const rect = event.currentTarget.getBoundingClientRect()
      const pad = 10
      this.popover.x = rect.left + rect.width / 2
      this.popover.y = rect.bottom + pad
    },

    clearHoverTimer() {
      if (this.hoverCloseTimer) {
        clearTimeout(this.hoverCloseTimer)
        this.hoverCloseTimer = null
      }
    },

    scheduleClosePreview() {
      this.clearHoverTimer()
      if (this.popover.locked) return

      this.hoverCloseTimer = setTimeout(() => {
        if (this.popover.locked) return
        if (this.hoveringChip) return
        if (this.popoverHover) return
        this.popover.open = false
      }, 200)
    },

    onChipEnter(m, item, event) {
      if (this.popover.locked) return
      this.clearHoverTimer()
      this.hoveringChip = true
      this.positionPopover(event)
      this.popover.open = true
      this.popover.member = m
      this.popover.item = item
    },

    onChipLeave() {
      this.hoveringChip = false
      this.scheduleClosePreview()
    },

    onPopoverEnter() {
      this.clearHoverTimer()
      this.popoverHover = true
    },

    onPopoverLeave() {
      this.popoverHover = false
      this.scheduleClosePreview()
    },

    toggleTeamLock(m, item, event) {
      this.clearHoverTimer()

      const same =
        this.popover.locked &&
        this.popover.member?.key === m.key &&
        this.popover.item?.conditionId === item.conditionId

      if (same) {
        this.popover.locked = false
        this.popover.open = false
        return
      }

      this.positionPopover(event)
      this.popover.open = true
      this.popover.locked = true
      this.popover.member = m
      this.popover.item = item
    },

    handleOutsideClick(e) {
      if (!this.popover.locked) return
      if (e.target.closest(".team-popover-portal")) return
      if (e.target.closest(".team-chip")) return
      this.popover.locked = false
      this.popover.open = false
    }
  }
}
</script>

<style scoped>
.team-compact {
  display: flex;
  gap: 10px;
  align-items: center;
}

.team-chip {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.chip-avatar {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid var(--border-lt);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.chip-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chip-fallback {
  font-weight: 1000;
  font-size: 10px;
  color: var(--primary);
}

.chip-role {
  font-size: 10px;
  font-weight: 1000;
  color: var(--text-secondary-on-background);
}

/* ✅ portal popover */
.team-popover-portal {
  position: fixed;
  transform: translateX(-50%);
  width: 240px;
  padding: 12px;
  border-radius: 14px;
  background: var(--background);
  border: 1px solid var(--border-ltr);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  z-index: 999999;
}

.popover-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.popover-avatar {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid var(--border-lt);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.popover-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.popover-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.popover-role {
  font-size: 10px;
  font-weight: 1000;
  color: var(--text-secondary-on-background);
  text-transform: uppercase;
}

.popover-name {
  font-size: 12px;
  font-weight: 950;
  color: var(--text-primary-on-background);
}

.popover-actions {
  display: flex;
  gap: 8px;
}

.popover-btn {
  flex: 1;
  border: 1px solid var(--border-lt);
  background: var(--button);
  color: var(--on-button);
  border-radius: 12px;
  padding: 7px 8px;
  font-size: 12px;
  font-weight: 950;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.popover-btn:hover {
  border-color: var(--primary);
  background: rgba(5, 29, 58, 0.08);
  color: var(--primary);
}

.popover-foot {
  margin-top: 10px;
  font-size: 11px;
  font-weight: 850;
  color: var(--text-secondary-on-background);
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.9;
}
.i {
  color: var(--notification-blue) !important;
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
}

</style>
